import { TimeTrackingSessionResource } from 'dy-frontend-http-repository/lib/modules/TimeTacking/resources';

// Injected so the orchestration below is a plain function over its effects rather than
// something only reachable through a mounted component.
export interface TimerRefreshOutcome {
    ok: boolean;
}

export interface TimerStopOutcome {
    // What the server actually logged. The stop is idempotent, so a repeat of a request that
    // landed returns the same entry and the same figure.
    duration_min: number;
}

export interface TimerOperationDeps {
    stopSession: (id: ID, comment: string) => Promise<TimerStopOutcome>;
    startSession: (subTicketId: ID) => Promise<TimeTrackingSessionResource>;
    onSessionStarted: (session: TimeTrackingSessionResource) => void;
    onSessionCleared: () => void;
    refresh: () => Promise<TimerRefreshOutcome>;

    // Puts the UI back into restoration before any recovery read is issued, so Start is never
    // offered in the gap between a refusal and the authoritative answer
    beginRestore: () => void;

    // Re-reads the caller's open session from the server and RETURNS it. The returned value is
    // the only thing that can settle whether a start that failed ambiguously actually committed,
    // so it is inspected rather than merely written to a store.
    restoreActiveSession: () => Promise<TimeTrackingSessionResource | null>;
}

// What the authoritative re-read established about a start that appeared to fail.
export type StartRecovery =
    // No start was attempted, or the start plainly succeeded — nothing to recover
    | 'not-attempted'
    // A session is open on the very phase the start targeted. Whatever the transport said, the
    // start committed: this is an effective success and must not be reported as a failure.
    | 'started-here'
    // A session is open, but on a different phase. The start was genuinely refused, and the
    // conflict can be named and linked.
    | 'running-elsewhere'
    // The server confirms no session is open. The start really did fail and can be retried.
    | 'not-started'
    // The recovery read itself failed. Nothing is known — neither success nor failure may be
    // claimed, and Start stays withheld until a later read succeeds.
    | 'unverified';

// Who holds the words the designer typed, so the copy can say so truthfully.
export type ReportCustody =
    // No report was in play (a plain Start)
    | 'none'
    // The write did not commit; the text is still in the box and is the only copy
    | 'retained'
    // The write committed; the text was saved against that session and cleared from the box
    | 'saved';

export interface TimerOperationResult {
    // The WRITE succeeded. The caller clears the typed report text on this and nothing else.
    //
    // Deliberately independent of whether the views could be re-read afterwards: a stop that
    // committed server-side is a committed stop even if the list refresh then fails or throws.
    // Folding the two together would tell a designer their work was not saved when it was, and
    // invite them to stop a session that no longer exists.
    ok: boolean;
    didStop: boolean;

    // True when a session is open on the target phase — whether the start's own response said so
    // or the authoritative recovery read did
    didStart: boolean;

    errorMessage: string | null;

    // What the recovery read established, and what it found. `recoveredSession` is the
    // authoritative value, so a caller can name and link a conflicting session without guessing.
    startRecovery: StartRecovery;
    recoveredSession: TimeTrackingSessionResource | null;

    // Minutes the server logged for the stop, or null when nothing was stopped
    loggedDurationMin: number | null;

    reportCustody: ReportCustody;

    // View truth, reported separately so the caller can offer a refresh retry without
    // implying the timer write failed
    refreshOk: boolean;
    refreshAttempted: boolean;
}

export const STOP_FAILED_MESSAGE =
    'The timer could not be stopped. Your report text has been kept — please try again.';

export const SWITCH_STOP_FAILED_MESSAGE =
    'The running timer could not be stopped, so no new timer was started. Your report text has been kept.';

// Every post-stop start outcome names the committed stop first, because that is the fact the
// designer most needs and the one they are most likely to doubt. None of them says the report
// text was "kept": it was saved against the session that closed, and the box is now empty.
export const SWITCH_START_FAILED_MESSAGE =
    'The previous timer was stopped and its report saved, but the new timer could not be started. You can start it again.';

export const SWITCH_START_CONFLICT_MESSAGE =
    'The previous timer was stopped and its report saved. A timer is already running elsewhere, so no new one was started here.';

export const SWITCH_START_UNVERIFIED_MESSAGE =
    'The previous timer was stopped and its report saved. We could not verify whether the new timer started — reload before starting another.';

// A plain Start carries no report at all, so none of these mention report custody
export const START_FAILED_MESSAGE = 'The timer could not be started. Please try again.';

export const START_CONFLICT_MESSAGE =
    'A timer is already running elsewhere, so a new one was not started here.';

// Deliberately neutral. The start request errored and the recovery read ALSO failed, so whether
// a session is now open is genuinely unknown — saying "could not be started" would assert a
// failure the client cannot see, and would invite a second Start over a timer that may well be
// running. The only honest statement is that verification failed.
export const START_UNVERIFIED_MESSAGE =
    'We could not verify whether the timer started. Reload before starting another timer.';

// Refreshing is never allowed to turn a committed write into a reported failure
const safeRefresh = async (deps: TimerOperationDeps): Promise<boolean> => {
    try {
        return (await deps.refresh()).ok;
    } catch (e) {
        console.error(e);

        return false;
    }
};

// A start that failed is genuinely ambiguous. A 409 raised because a concurrent tab opened a
// session, and a request that timed out after the server had already created one, are
// indistinguishable from here — and guessing either way is a real hazard: assume it failed and
// the designer starts a second session, assume it succeeded and they walk away from a timer that
// is not running.
//
// So nothing is guessed. Restoration is entered FIRST — Start stays disabled from this instant,
// not from whenever the read is issued — the server is asked, and the ANSWER IS INSPECTED. A
// session on the target phase means the start committed after all; a session elsewhere is a real
// conflict that can be named; a confirmed null is a real failure; and a failed read leaves the
// state unknown, which is its own outcome and never reported as either.
const recoverStartTruth = async (
    deps: TimerOperationDeps,
    targetSubTicketId: ID
): Promise<{ recovery: StartRecovery; session: TimeTrackingSessionResource | null }> => {
    try {
        deps.beginRestore();
    } catch (e) {
        console.error(e);
    }

    try {
        const session = await deps.restoreActiveSession();

        if (!session) {
            return { recovery: 'not-started', session: null };
        }

        return {
            recovery: session.task_sub_ticket_id === targetSubTicketId ? 'started-here' : 'running-elsewhere',
            session,
        };
    } catch (e) {
        console.error(e);

        return { recovery: 'unverified', session: null };
    }
};

// Stop & Save. The server's stop is idempotent — a repeat returns the same entry id and writes
// nothing — so retrying a request that actually landed cannot double-log.
export const stopRunningTimer = async (
    session: TimeTrackingSessionResource,
    comment: string,
    deps: TimerOperationDeps
): Promise<TimerOperationResult> => {
    let stopped: TimerStopOutcome;

    try {
        stopped = await deps.stopSession(session.id, comment);
        deps.onSessionCleared();
    } catch (e) {
        console.error(e);

        // The session is deliberately left in place: it is still open server-side, and dropping
        // it locally would hide the only control that can close it
        return {
            ok: false, didStop: false, didStart: false, errorMessage: STOP_FAILED_MESSAGE,
            startRecovery: 'not-attempted', recoveredSession: null,
            loggedDurationMin: null, reportCustody: 'retained',
            refreshOk: true, refreshAttempted: false,
        };
    }

    // The stop has already committed by this point; the refresh can only affect view truth
    const refreshOk = await safeRefresh(deps);

    return {
        ok: true, didStop: true, didStart: false, errorMessage: null,
        startRecovery: 'not-attempted', recoveredSession: null,
        loggedDurationMin: stopped.duration_min, reportCustody: 'saved',
        refreshOk, refreshAttempted: true,
    };
};

// A plain Start, with the same authoritative recovery as the post-stop one. Nothing was typed
// and nothing was stopped, so no message here mentions report custody.
export const startTimerOnSubTicket = async (
    targetSubTicketId: ID,
    deps: TimerOperationDeps
): Promise<TimerOperationResult> => {
    try {
        const started = await deps.startSession(targetSubTicketId);
        deps.onSessionStarted(started);

        const refreshOk = await safeRefresh(deps);

        return {
            ok: true, didStop: false, didStart: true, errorMessage: null,
            startRecovery: 'not-attempted', recoveredSession: started,
            loggedDurationMin: null, reportCustody: 'none',
            refreshOk, refreshAttempted: true,
        };
    } catch (e) {
        console.error(e);

        const { recovery, session } = await recoverStartTruth(deps, targetSubTicketId);

        // The start committed after all. Reporting the transport's complaint would send the
        // designer to press Start again over a session that is already running.
        if (recovery === 'started-here') {
            const refreshOk = await safeRefresh(deps);

            return {
                ok: true, didStop: false, didStart: true, errorMessage: null,
                startRecovery: recovery, recoveredSession: session,
                loggedDurationMin: null, reportCustody: 'none',
                refreshOk, refreshAttempted: true,
            };
        }

        const refreshOk = await safeRefresh(deps);

        return {
            ok: false, didStop: false, didStart: false,
            errorMessage:
                recovery === 'running-elsewhere'
                    ? START_CONFLICT_MESSAGE
                    : recovery === 'unverified'
                      ? START_UNVERIFIED_MESSAGE
                      : START_FAILED_MESSAGE,
            startRecovery: recovery, recoveredSession: session,
            loggedDurationMin: null, reportCustody: 'none',
            refreshOk, refreshAttempted: true,
        };
    }
};

// Switching timers finalises real work against a different phase, so the previous session gets
// its own report text. If that stop fails, no new timer is started — starting one anyway would
// leave two open sessions and silently orphan the first.
export const switchTimerToSubTicket = async (
    session: TimeTrackingSessionResource,
    targetSubTicketId: ID,
    previousSessionComment: string,
    deps: TimerOperationDeps
): Promise<TimerOperationResult> => {
    let stopped: TimerStopOutcome;

    try {
        stopped = await deps.stopSession(session.id, previousSessionComment);
        deps.onSessionCleared();
    } catch (e) {
        console.error(e);
        const refreshOk = await safeRefresh(deps);

        return {
            ok: false, didStop: false, didStart: false, errorMessage: SWITCH_STOP_FAILED_MESSAGE,
            startRecovery: 'not-attempted', recoveredSession: null,
            loggedDurationMin: null, reportCustody: 'retained',
            refreshOk, refreshAttempted: true,
        };
    }

    // From here the stop is committed and the previous report is saved, whatever happens next.
    // Both facts are carried into every branch below untouched.
    try {
        deps.onSessionStarted(await deps.startSession(targetSubTicketId));
    } catch (e) {
        console.error(e);

        // Recovery comes first and is awaited: whether a session is open right now is the
        // question the designer is about to act on, and it outranks re-reading the list
        const { recovery, session: recovered } = await recoverStartTruth(deps, targetSubTicketId);
        const refreshOk = await safeRefresh(deps);

        // The start committed after all — the switch as a whole succeeded
        if (recovery === 'started-here') {
            return {
                ok: true, didStop: true, didStart: true, errorMessage: null,
                startRecovery: recovery, recoveredSession: recovered,
                loggedDurationMin: stopped.duration_min, reportCustody: 'saved',
                refreshOk, refreshAttempted: true,
            };
        }

        return {
            ok: false, didStop: true, didStart: false,
            errorMessage:
                recovery === 'running-elsewhere'
                    ? SWITCH_START_CONFLICT_MESSAGE
                    : recovery === 'unverified'
                      ? SWITCH_START_UNVERIFIED_MESSAGE
                      : SWITCH_START_FAILED_MESSAGE,
            startRecovery: recovery, recoveredSession: recovered,
            loggedDurationMin: stopped.duration_min, reportCustody: 'saved',
            refreshOk, refreshAttempted: true,
        };
    }

    // Both writes committed; only view truth remains in question
    const refreshOk = await safeRefresh(deps);

    return {
        ok: true, didStop: true, didStart: true, errorMessage: null,
        startRecovery: 'not-attempted', recoveredSession: null,
        loggedDurationMin: stopped.duration_min, reportCustody: 'saved',
        refreshOk, refreshAttempted: true,
    };
};
